import { ExpandCollapseAnimation } from '@doltech/ui/lib/examples/Animation/ExpandCollapse';
import {
  FlatAudioPlayer,
  FlatPlayerInstance,
} from '@doltech/ui/lib/figma/Audio/Player/FlatAudioPlayer';
import { TestTypeEnum, zIndex } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { useControlledAudioPlayInBackgroundStore } from '@doltech/ui/lib/hooks/controlled-audio-play-in-background.store';
import { useCountDownTimer } from '@doltech/ui/lib/hooks/useCountDownTimer';
import { submitTest } from '../../api/do-test';
import { useListeningAudioControlledStore } from '../../hooks/useListeningAudioControlledStore';
import { useListeningFullTestModal } from '../../hooks/useListeningFullTestModal';
import { BottomNavigationLayout } from '../../layouts/BottomNavigationLayout';
import { useTestSubmissionStore } from '../../model/test-submissions.hook';
import { QuestionBar } from '../QuestionBar';
import { PracticeTestListeningBottomNavigation } from './LayoutModePracticeTest/PracticeTestListeningBottomNavigation';
import { SectionInfoListeningFullTest } from './SectionInfoListeningFullTest';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';

interface BottomNavigationProps extends React.HTMLAttributes<HTMLDivElement> {
  disableTestInfo?: boolean;
  disableSubmitAction?: boolean;
  onSubmitWithEvents?: () => void;
  onSubmitSuccess?: () => void;
  isPlayAudio?: boolean;
}

const Main = styled.div`
  position: relative;
  background-color: ${colorsV2.white100};

  .audio-player-wrapper {
    position: absolute;
    left: 0;
    right: 0;
    top: -12px;
    z-index: ${zIndex.AUDIO_PLAYER_WRAPPER};
  }
`;

const FullTestListeningBottomNavigation = observer((props: BottomNavigationProps) => {
  const { children, disableTestInfo, disableSubmitAction, onSubmitSuccess, ...rest } = props;
  const audioPlayerRef = React.useRef<FlatPlayerInstance>();
  const store = useTestSubmissionStore();
  const {
    timeTakenInSeconds,
    id,
    totalDurationInSeconds,
    prepareDataForSubmit,
    setRemainTimeInSeconds,
    remainTimeInSeconds,
    audio,
  } = store;
  const audioStore = useControlledAudioPlayInBackgroundStore();
  const { openModalConfirmSubmit } = useListeningFullTestModal();

  useListeningAudioControlledStore({
    audioPath: audio?.path,
  });

  const handleSubmitTest = async () => {
    submitTest({ testId: id, userAnswers: prepareDataForSubmit() });
  };

  const { startTimer, stopTimer } = useCountDownTimer(
    remainTimeInSeconds as any,
    (timeInSeconds) => {
      setRemainTimeInSeconds(timeInSeconds);

      if (!audioStore?.playing && timeTakenInSeconds + 1 < audioStore.durationInSeconds) {
        audioStore?.play?.(timeTakenInSeconds);
      }
      if (timeInSeconds <= 0) {
        audioStore?.pause?.();
        stopTimer();
        handleSubmitTest();

        openModalConfirmSubmit({
          title: 'Hết giờ',
          description: 'Đã hết giờ làm bài. Hệ thống sẽ tự động nộp bài.',
          primaryButton: {
            name: 'OK',
            onClick: onSubmitSuccess,
          },
        });
      }
    },
    1000
  );

  React.useEffect(() => {
    if (store.isStartedDoingTest) {
      if (timeTakenInSeconds < audioStore.durationInSeconds) {
        audioStore.play(timeTakenInSeconds);
      }
      startTimer();
      store.startSection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.isStartedDoingTest]);

  return (
    <Main {...rest}>
      <BottomNavigationLayout className="listening-full-test">
        <div className="bottom-nav-wrapper">
          {store.isReviewing ? null : (
            <div className="audio-player-wrapper">
              <FlatAudioPlayer
                startTimeInSeconds={store?.currentTestSection?.testTakenTime || 0}
                ref={audioPlayerRef}
                volume={1}
                testTimeTakenInSeconds={timeTakenInSeconds}
                testDurationInSeconds={totalDurationInSeconds}
                customSlider
                url={null}
              />
            </div>
          )}

          <ExpandCollapseAnimation expanded={true}>
            <div className="all-question-info">
              <QuestionBar />
            </div>
          </ExpandCollapseAnimation>
          <div {...rest}>
            <SectionInfoListeningFullTest {...props}>{children}</SectionInfoListeningFullTest>
          </div>
        </div>
      </BottomNavigationLayout>
    </Main>
  );
});

export const ListeningBottomNavigation = observer((props: BottomNavigationProps) => {
  const store = useTestSubmissionStore();
  const { onSubmitWithEvents } = props;

  switch (store.testType) {
    case TestTypeEnum.PRACTICE_TEST: {
      return (
        <PracticeTestListeningBottomNavigation onSubmitWithEvents={onSubmitWithEvents} {...props} />
      );
    }
    case TestTypeEnum.FULL_TEST:
      return <FullTestListeningBottomNavigation {...props} />;
    default:
  }
  return null;
});
